import { useState } from 'react';
import FileUpload from 'components/FileUpload';
import DownloadBox from 'components/DownloadBox';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';

// Image Compressor — v2: compresses ENTIRELY IN THE BROWSER (no server).
// Two modes:
//   1) Target size (KB)  — binary-searches JPEG quality (and scales down
//      dimensions if needed) to fit under an exact KB limit. Perfect for
//      exam forms that demand "max 50KB" etc.
//   2) Quality slider    — classic percentage-based compression.
// Guarantees: output is never larger than the original file.

function fmtSize(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

// Decode a File into a drawable bitmap (handles EXIF rotation).
async function decodeImage(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) { /* fall through to <img> path */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image decode failed')); };
    img.src = url;
  });
}

// Draw the image at a given scale and encode as JPEG at a given quality.
function renderJpeg(source, scale, quality) {
  return new Promise((resolve, reject) => {
    const w = Math.max(1, Math.round((source.width || source.naturalWidth) * scale));
    const h = Math.max(1, Math.round((source.height || source.naturalHeight) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    // White background so transparent PNGs don't turn black in JPEG
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(source, 0, 0, w, h);
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Encode failed')),
      'image/jpeg',
      quality
    );
  });
}

// Find the largest quality that fits under targetBytes.
// If even minimum quality is too big, progressively scale dimensions down.
async function compressToTarget(source, targetBytes) {
  let scale = 1;
  for (let attempt = 0; attempt < 5; attempt++) {
    let lo = 0.05, hi = 0.95, best = null;
    for (let i = 0; i < 7; i++) {
      const q = (lo + hi) / 2;
      const blob = await renderJpeg(source, scale, q);
      if (blob.size <= targetBytes) { best = blob; lo = q; } else { hi = q; }
    }
    if (best) return best;
    scale *= 0.7; // shrink the image and try again
  }
  // Last resort: smallest possible
  return renderJpeg(source, scale, 0.05);
}

const KB_PRESETS = [20, 50, 100, 200];

export default function ImageCompress() {
  const [file, setFile]         = useState(null);
  const [mode, setMode]         = useState('target');   // 'target' | 'quality'
  const [targetKb, setTargetKb] = useState(50);
  const [quality, setQuality]   = useState(70);
  const [preview, setPreview]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [dlUrl, setDlUrl]       = useState('');
  const [outName, setOutName]   = useState('compressed.jpg');
  const [stats, setStats]       = useState(null);

  const handleFile = f => {
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : '');
    setDlUrl(''); setStats(null); setError('');
  };

  const reset = () => {
    setFile(null); setPreview(''); setError(''); setDlUrl(''); setStats(null);
  };

  const handle = async () => {
    if (!file) { setError('Please select an image file.'); return; }
    if (mode === 'target' && (!targetKb || targetKb < 5)) {
      setError('Please enter a target size of at least 5 KB.');
      return;
    }
    setLoading(true); setError(''); setDlUrl(''); setStats(null);

    try {
      const source = await decodeImage(file);

      let blob;
      if (mode === 'target') {
        blob = await compressToTarget(source, targetKb * 1024);
      } else {
        blob = await renderJpeg(source, 1, quality / 100);
      }

      // Never hand back a file larger than the original.
      if (blob.size >= file.size) blob = file;

      const origSize = file.size;
      const newSize  = blob.size;
      const savedPct = Math.max(0, Math.round((1 - newSize / origSize) * 100));

      setStats([
        { value: fmtSize(origSize), label: 'Original Size' },
        { value: fmtSize(newSize),  label: 'New Size' },
        { value: savedPct + '%',    label: 'Size Reduced' },
      ]);

      const base = (file.name || 'image').replace(/\.[^.]+$/, '');
      setOutName(`${base}-compressed.jpg`);
      setDlUrl(URL.createObjectURL(blob));
    } catch (err) {
      setError('Compression failed. Please make sure the file is a valid image and try again.');
    } finally { setLoading(false); }
  };

  const getQualityLabel = () => {
    if (quality >= 80) return { text: 'High Quality', color: 'var(--green)' };
    if (quality >= 50) return { text: 'Balanced', color: '#d97706' };
    return { text: 'Maximum Compression', color: 'var(--red)' };
  };
  const lbl = getQualityLabel();

  const modeBtn = (id, label) => (
    <button key={id}
      onClick={() => setMode(id)}
      style={{
        flex: 1, padding: '9px 14px', borderRadius: 8, cursor: 'pointer',
        fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 600,
        border: mode === id ? '2px solid var(--blue-dd)' : '1.5px solid var(--slate-200)',
        background: mode === id ? 'var(--blue-dd)' : '#fff',
        color: mode === id ? '#fff' : 'var(--slate-600)',
        transition: 'all .15s',
      }}>
      {label}
    </button>
  );

  return (
    <>
    <Seo title="Compress Image to 50KB, 100KB Online Free" description="Compress JPG/PNG to an exact size — 20KB, 50KB, 100KB — for exam forms. Free, private, no signup. Files never leave your browser." path="/tools/image/compress" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon cat-image"><Icon name="compress" size={24} /></div>
            <div>
              <h1>Image Compressor</h1>
              <p>Compress to an exact size (50KB, 100KB) for exam forms, or use the quality slider. Runs in your browser — nothing is uploaded.</p>
            </div>
          </div>
        </div>

        {!dlUrl ? (
          <div className="tool-box">
            <FileUpload
              accept="image/*"
              onFileChange={handleFile}
              hint="Supported: JPG, PNG, WEBP · Max 20MB"
            />

            {preview && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <img
                  src={preview}
                  alt="preview"
                  style={{ maxHeight: 180, maxWidth: '100%', borderRadius: 8, border: '1.5px solid var(--slate-200)', objectFit: 'contain' }}
                />
                <p style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 6 }}>
                  Original size: <strong>{fmtSize(file?.size)}</strong>
                </p>
              </div>
            )}

            {/* Mode switch */}
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              {modeBtn('target', 'Target Size (KB)')}
              {modeBtn('quality', 'Quality Slider')}
            </div>

            {mode === 'target' ? (
              <div className="form-group" style={{ marginTop: 16 }}>
                <label>Compress to under:</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {KB_PRESETS.map(kb => (
                    <button key={kb}
                      onClick={() => setTargetKb(kb)}
                      style={{
                        padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
                        fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 700,
                        border: targetKb === kb ? '2px solid var(--blue-dd)' : '1.5px solid var(--slate-200)',
                        background: targetKb === kb ? 'var(--blue-50)' : '#fff',
                        color: targetKb === kb ? 'var(--blue-dd)' : 'var(--slate-600)',
                      }}>
                      {kb} KB
                    </button>
                  ))}
                  <input
                    type="number" min="5" max="5000"
                    className="form-control"
                    value={targetKb}
                    onChange={e => setTargetKb(Number(e.target.value))}
                    style={{ width: 110, textAlign: 'center' }}
                    placeholder="Custom KB"
                  />
                </div>
                <p style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 8 }}>
                  Perfect for exam forms — SSC/UPSC photo (50KB), signature (20KB), documents (100–200KB).
                </p>
              </div>
            ) : (
              <div className="form-group" style={{ marginTop: 16 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Quality: <strong>{quality}%</strong></span>
                  <span style={{ color: lbl.color, fontWeight: 700, fontSize: 13 }}>{lbl.text}</span>
                </label>
                <input
                  type="range" min="10" max="95"
                  value={quality}
                  onChange={e => setQuality(Number(e.target.value))}
                  style={{ width: '100%', marginTop: 10, accentColor: 'var(--blue)', height: 6 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--slate-400)', marginTop: 4, fontWeight: 600 }}>
                  <span>10% — Smallest file</span>
                  <span>95% — Best quality</span>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-error">
                <Icon name="alertCircle" size={16} />{error}
              </div>
            )}

            <button
              className="btn btn-primary btn-lg btn-full"
              style={{ marginTop: 8 }}
              onClick={handle}
              disabled={loading || !file}
            >
              {loading
                ? <><span className="spinner sp-sm" />Compressing...</>
                : <><Icon name="compress" size={18} />
                    {mode === 'target' ? `Compress to under ${targetKb} KB` : `Compress Image (${quality}% quality)`}
                  </>
              }
            </button>
          </div>
        ) : (
          <DownloadBox
            url={dlUrl}
            filename={outName}
            onReset={reset}
            stats={stats}
          />
        )}

        <div className="alert alert-info" style={{ marginTop: 14 }}>
          <Icon name="info" size={16} />
          <span>
            Your image is compressed <strong>entirely in your browser</strong> — it is never uploaded
            to any server. Use <strong>Target Size</strong> mode to hit exact limits like 50KB for
            SSC/UPSC photo uploads. Output is saved as JPG.
          </span>
        </div>
         <ToolSeoContent tool="image-compress" />
      </div>
    </div>
  </>
  );
}